import { Component } from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Minimal error boundary. Wrap around any subtree where a render error
 * would otherwise unmount a critical parent (forms with in-progress
 * user input, dashboards, etc.).
 *
 * Catches errors during render, lifecycle, and child constructors —
 * NOT in event handlers / async callbacks (those use try/catch).
 *
 * Designed to be drop-in: <ErrorBoundary>{children}</ErrorBoundary>
 *   Optional props:
 *     • fallback: ReactNode — replaces the default error UI
 *     • onReset:  () => void — handler for the "Try again" button
 *     • label:    string  — short label shown in the default UI ("Health tab", etc.)
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log so production debugging in Sentry/console is straightforward
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.label ? `:${this.props.label}` : ""}]`, error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === "function") this.props.onReset();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const message = this.state.error?.message || "Something went wrong rendering this panel.";
    return (
      <div
        className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-sm text-rose-900"
        data-testid="error-boundary-fallback"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold mb-1">
              {this.props.label ? `${this.props.label} couldn't render.` : "This panel couldn't render."}
            </div>
            <div className="text-rose-800 mb-3 font-mono text-xs break-all">{message}</div>
            <button
              type="button"
              onClick={this.handleReset}
              className="px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium transition-colors"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
